/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useState, useRef } from 'react';
import {
  Button,
  Flex,
  Spinner,
  Text,
  Tooltip,
  HStack,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  IconButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useQuery } from '@tanstack/react-query';
import { MdStickyNote2 } from 'react-icons/md';
import { createSearchParams } from 'react-router-dom';
import { FaNotEqual } from 'react-icons/fa6';
import moment from 'moment';
import 'moment/locale/pt-br';
import { IoCloseCircleOutline, IoHelpCircleOutline } from 'react-icons/io5';
import { CiCircleCheck } from 'react-icons/ci';
import { Package, FunnelSimple } from 'phosphor-react';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { SearchModeBar, MODES_ID_NOME_CNPJ } from '../../../../components/SearchModeBar';
import { FormBox } from '../../../../components/formBox';
import SelectInputForm from '../../../../components/inputs/forms/SelectInputForm';
import MultiSelectInputForm from '../../../../components/inputs/forms/MultiSelectInputForm';
import { IData, IPagination, ISelect } from '../../../../types/shared';
import { ICompany, IApiSiegTotais, useCompanies } from '../../../../hooks/useCompanies';
import { IClient } from '../../../../hooks/useClients';
import { formatCNPJ, getFormattedLabelString } from '../../../../utils/FormatFunctions';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { PageBox } from '../../../../components/pageBox';
import ViewNotaModal from '../../../../components/viewNota';
import { FilterDrawer } from '../../../../components/FilterDrawer';

moment.locale('pt-br');

export function ModulesHomeIntegrationApiXSiegPage() {
  const { getCompanies, getCompaniesFilters, getAPIXSiegTotais } = useCompanies();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [searchMode, setSearchMode] = useState<'ID' | 'Nome' | 'CNPJ'>('ID');
  const searchModeRef = useRef(searchMode);

  useEffect(() => {
    searchModeRef.current = searchMode;
  }, [searchMode]);

  const {
    currentDate,
    dateStart,
    dateEnd,
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
    filters,
    setFilters,
  } = useMonthFilter({
    additionalParams: {
      search: '',
      screen_data: 'api_sieg',
    },
  });

  const filtersFormMethods = useForm<{
    search: string;
    cnpj: string;
    companies: ICompany;
    cliente: IClient;
    cidade: ISelect<string>;
    uf: ISelect<string>;
    grupo_de_empresas: ISelect<string>[];
    tags: ISelect<string>;
    comercial: ISelect<string>;
    contabil: ISelect<string>;
    financeiro: ISelect<string>;
    contas_a_pagar: ISelect<string>;
    encerramento_anual: ISelect<string>;
    fiscal: ISelect<string>;
    imposto_de_renda: ISelect<string>;
    integracao_contabil: ISelect<string>;
    integracao_fiscal: ISelect<string>;
    pessoal: ISelect<string>;
    relacionamento_com_cliente: ISelect<string>;
    societario: ISelect<string>;
  }>({
    mode: 'onChange',
    defaultValues: {
      search: filters.get('search') ?? filters.get('external_id') ?? '',
      cnpj: filters.get('cnpj') ?? '',
    },
  });

  // Verificar se há filtros ativos (excluindo campos básicos)
  const hasActiveFilters = Array.from(filters.keys()).some(
    key =>
      key !== 'page' &&
      key !== 'size' &&
      key !== 'screen_data' &&
      key !== 'date_start' &&
      key !== 'date_end' &&
      key !== 'situacao' &&
      key !== 'search' &&
      key !== 'cnpj' &&
      filters.get(key),
  );

  // Debounce para o campo de busca
  const debouncedSearch = useDebounceCallback((searchValue: string) => {
    const newFilters = new URLSearchParams(filters);
    const currentMode = searchModeRef.current;

    // Remove todos os parâmetros de busca anteriores
    newFilters.delete('search');
    newFilters.delete('cnpj');
    newFilters.delete('external_id');

    if (searchValue) {
      switch (currentMode) {
        case 'ID':
          newFilters.set('external_id', searchValue);
          break;
        case 'Nome':
          newFilters.set('search', searchValue);
          break;
        case 'CNPJ':
          newFilters.set('cnpj', searchValue.replace(/[^0-9A-Za-z]/g, '').toUpperCase());
          break;
        default:
          break;
      }
    }

    newFilters.set('page', '1');
    setFilters(newFilters);
  }, 500);

  // Re-executa busca quando muda o modo
  useEffect(() => {
    debouncedSearch(filtersFormMethods.getValues().search || '');
  }, [searchMode]);

  // Watch no campo search
  useEffect(() => {
    const subscription = filtersFormMethods.watch((values, { name }) => {
      if (name === 'search') {
        debouncedSearch(values.search || '');
      }
    });

    return () => subscription.unsubscribe();
  }, [filtersFormMethods.watch, debouncedSearch]);

  // Função para aplicar os filtros quando clica no botão "Filtrar"
  const handleApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    const newFilters = new URLSearchParams();

    // Mantém filtros básicos
    newFilters.set('page', '1');
    newFilters.set('size', '50');
    newFilters.set('search', filters.get('search') || '');
    if (filters.get('cnpj')) newFilters.set('cnpj', filters.get('cnpj') || '');
    newFilters.set('screen_data', 'api_sieg');
    newFilters.set(
      'date_start',
      filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
    );
    newFilters.set(
      'date_end',
      filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
    );

    // Mantém situacao se existir
    const situacao = filters.get('situacao');
    if (situacao) newFilters.set('situacao', situacao);

    // Aplica filtros avançados
    if (values.companies?.external_id) newFilters.set('id', values.companies.external_id);
    if (values.cidade?.value) newFilters.set('cidade', values.cidade.value);
    if (values.uf?.value) newFilters.set('uf', values.uf.value);
    if (values.grupo_de_empresas && values.grupo_de_empresas.length > 0) {
      const gruposValues = values.grupo_de_empresas.map(g => g.value).join(',');
      newFilters.set('grupo_de_empresas', gruposValues);
    }
    if (values.tags?.value) newFilters.set('tags', values.tags.value);
    if (values.comercial?.value) newFilters.set('comercial', values.comercial.value);
    if (values.contabil?.value) newFilters.set('contabil', values.contabil.value);
    if (values.financeiro?.value) newFilters.set('financeiro', values.financeiro.value);
    if (values.contas_a_pagar?.value) newFilters.set('contas_a_pagar', values.contas_a_pagar.value);
    if (values.encerramento_anual?.value)
      newFilters.set('encerramento_anual', values.encerramento_anual.value);
    if (values.fiscal?.value) newFilters.set('fiscal', values.fiscal.value);
    if (values.imposto_de_renda?.value)
      newFilters.set('imposto_de_renda', values.imposto_de_renda.value);
    if (values.integracao_contabil?.value)
      newFilters.set('integracao_contabil', values.integracao_contabil.value);
    if (values.integracao_fiscal?.value)
      newFilters.set('integracao_fiscal', values.integracao_fiscal.value);
    if (values.pessoal?.value) newFilters.set('pessoal', values.pessoal.value);
    if (values.relacionamento_com_cliente?.value)
      newFilters.set('relacionamento_com_cliente', values.relacionamento_com_cliente.value);
    if (values.societario?.value) newFilters.set('societario', values.societario.value);

    setFilters(newFilters);
  };

  // Função para limpar todos os filtros
  const handleClearFilters = () => {
    const clearedFilters = createSearchParams({
      page: '1',
      size: '50',
      search: filters.get('search') || '',
      date_start: filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
      date_end: filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
      screen_data: 'api_sieg',
    });
    // Mantém situacao e cnpj se existirem
    const situacao = filters.get('situacao');
    const cnpj = filters.get('cnpj');
    if (situacao) clearedFilters.set('situacao', situacao);
    if (cnpj) clearedFilters.set('cnpj', cnpj);

    setFilters(clearedFilters);
    // Limpar cada campo individualmente para forçar atualização visual
    const { setValue } = filtersFormMethods;
    setValue('companies', null as any);
    setValue('cidade', null as any);
    setValue('uf', null as any);
    setValue('grupo_de_empresas', []);
    setValue('tags', null as any);
    setValue('comercial', null as any);
    setValue('contabil', null as any);
    setValue('financeiro', null as any);
    setValue('contas_a_pagar', null as any);
    setValue('encerramento_anual', null as any);
    setValue('fiscal', null as any);
    setValue('imposto_de_renda', null as any);
    setValue('integracao_contabil', null as any);
    setValue('integracao_fiscal', null as any);
    setValue('pessoal', null as any);
    setValue('relacionamento_com_cliente', null as any);
    setValue('societario', null as any);
  };

  const queryKeyCompaniesFilters = ['CompaniesFilters'];
  const { data: CompaniesFiltersData } = useQuery({
    queryKey: queryKeyCompaniesFilters,
    queryFn: getCompaniesFilters,
  });

  const CompaniesFilters = CompaniesFiltersData;

  const { data: dataCompaniesList, isFetching: isFetchingCompanyList } = useQuery<
    IData<ICompany[]>
  >({
    queryKey: [
      'CompaniesList-apiXSieg',
      cleanFilters(Object.fromEntries([...Array.from(filters)])),
    ],
    queryFn: getCompanies,
  });

  const { items: companiesList, ...pagination } = dataCompaniesList?.data ?? {
    items: [],
    ...({} as IPagination),
  };

  const queryKeyQuebrasTotatais = [
    'getQuebrasTotais-apiXSieg',
    cleanFilters(Object.fromEntries([...Array.from(filters)])),
  ];

  const { data: quebrasTotais, isFetching: isFetchingQuebrasTotais } = useQuery<IApiSiegTotais>({
    queryKey: queryKeyQuebrasTotatais,
    queryFn: getAPIXSiegTotais,
  });

  return (
    <PageBox>
      <div className="flex flex-col gap-2">
        <div className="mb:20 flex w-full items-center justify-between">
          <div className="flex content-start lg:content-center">
            <ReturnButton previousPage="modulesHome/integration" />

            <div className="h-full">
              <div className="flex items-center justify-center rounded-full bg-[#fc6c4f] p-3">
                <FaNotEqual color="#ff4e2a" className="size-8" />
              </div>
            </div>
            <div className="flex w-full flex-col justify-center gap-1 pl-2">
              <Text fontWeight="bold" fontSize="16px">
                Comparativo de Importações
              </Text>
            </div>
          </div>
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <IconButton
                aria-label="Ajuda"
                icon={<IoHelpCircleOutline size={20} />}
                size="xs"
                variant="ghost"
                borderRadius="full"
                color="gray.500"
                _hover={{ color: 'blue.500', bg: 'blue.50' }}
              />
            </PopoverTrigger>
            <PopoverContent w="auto" maxW="500px">
              <PopoverArrow />
              <PopoverBody p={4}>
                <Text fontWeight="bold" fontSize="sm" mb={2}>
                  Regras de Comparação por Cidade/UF
                </Text>
                <Text fontSize="xs" color="gray.600" mb={3}>
                  A comparação entre SIEG, Domínio, Autmais e Portal Nac. considera regras
                  específicas por localidade. Em algumas cidades/UFs, determinadas fontes são
                  ignoradas por não possuírem dados confiáveis ou disponíveis.
                </Text>
                <TableContainer>
                  <Table size="sm" variant="simple">
                    <Thead>
                      <Tr>
                        <Th fontSize="xs" px={2}>
                          Condição
                        </Th>
                        <Th fontSize="xs" px={2} textAlign="center">
                          SIEG
                        </Th>
                        <Th fontSize="xs" px={2} textAlign="center">
                          Portal Nac.
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      <Tr>
                        <Td fontSize="xs" px={2}>
                          GO, DF
                        </Td>
                        <Td fontSize="xs" px={2} textAlign="center" color="red.500">
                          Não utilizado
                        </Td>
                        <Td fontSize="xs" px={2} textAlign="center" color="green.500">
                          Utilizado
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontSize="xs" px={2}>
                          Goiânia, Anápolis, Aparecida, Brasília
                        </Td>
                        <Td fontSize="xs" px={2} textAlign="center" color="red.500">
                          Não utilizado
                        </Td>
                        <Td fontSize="xs" px={2} textAlign="center" color="red.500">
                          Não utilizado
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontSize="xs" px={2}>
                          São Paulo (município)
                        </Td>
                        <Td fontSize="xs" px={2} textAlign="center" color="green.500">
                          Utilizado
                        </Td>
                        <Td fontSize="xs" px={2} textAlign="center" color="red.500">
                          Não utilizado
                        </Td>
                      </Tr>
                      <Tr>
                        <Td fontSize="xs" px={2}>
                          Outras localidades
                        </Td>
                        <Td fontSize="xs" px={2} textAlign="center" color="green.500">
                          Utilizado
                        </Td>
                        <Td fontSize="xs" px={2} textAlign="center" color="green.500">
                          Utilizado
                        </Td>
                      </Tr>
                    </Tbody>
                  </Table>
                </TableContainer>
                <Text fontSize="xs" color="gray.500" mt={3}>
                  Quando uma fonte é "Não utilizado", ela não é considerada na comparação de OK,
                  Erro ou Aviso para NFS-e de saída.
                </Text>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </div>
        {isFetchingQuebrasTotais ? (
          <div className="mt-4 flex flex-col items-center gap-4">
            <Spinner size="lg" color="primary" />
            <span className="text-xl font-semibold">Carregando...</span>
          </div>
        ) : (
          <>
            {/* Carrossel Superior - Resumo de Empresas */}
            <div className="mt-4 flex flex-row flex-nowrap gap-3 overflow-x-auto pb-2">
              {/* Card Total Empresas */}
              <Tooltip label="Total de empresas cadastradas no sistema" placement="top">
                <Flex className="flex items-center gap-2 rounded-2xl bg-indigo-50 p-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-100 p-2">
                    <Package color="#6366F1" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Total Empresas
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.total_empresas ?? 0}
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Com Dados */}
              <Tooltip
                label="Empresas que possuem notas fiscais registradas em qualquer fonte (SIEG, Domínio, Autmais ou Portal)"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'com_dados' ? 'bg-blue-200' : 'bg-blue-50'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'com_dados') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'com_dados');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 p-2">
                    <Package color="#3B82F6" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Com Dados
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.com_dados ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.com_dados_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Sem Dados */}
              <Tooltip
                label="Empresas sem notas fiscais registradas em nenhuma fonte"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'sem_dados' ? 'bg-gray-300' : 'bg-gray-100'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'sem_dados') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'sem_dados');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 p-2">
                    <Package color="#6B7280" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Sem Dados
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.sem_dados ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.sem_dados_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Empresas Saída */}
              <Tooltip
                label="Empresas com notas de saída em qualquer fonte: SIEG, Domínio, Autmais ou Portal > 0"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'empresas_saida' ? 'bg-cyan-200' : 'bg-cyan-50'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'empresas_saida') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'empresas_saida');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-100 p-2">
                    <Package color="#06B6D4" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Emp. Saída
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.empresas_saida ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.empresas_saida_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Empresas Entrada */}
              <Tooltip
                label="Empresas com notas de entrada em qualquer fonte: SIEG, Domínio, Autmais ou Portal > 0"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'empresas_entrada' ? 'bg-teal-200' : 'bg-teal-50'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'empresas_entrada') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'empresas_entrada');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 p-2">
                    <Package color="#14B8A6" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Emp. Entrada
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.empresas_entrada ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.empresas_entrada_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Empresas Sem Saída */}
              <Tooltip label="Empresas sem notas de saída em nenhuma fonte" placement="top">
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'empresas_s_saida' ? 'bg-rose-200' : 'bg-rose-50'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'empresas_s_saida') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'empresas_s_saida');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 p-2">
                    <Package color="#F43F5E" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Sem Saída
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.empresas_s_saida ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.empresas_s_saida_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Empresas Sem Entrada */}
              <Tooltip label="Empresas sem notas de entrada em nenhuma fonte" placement="top">
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'empresas_s_entrada'
                      ? 'bg-amber-200'
                      : 'bg-amber-50'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'empresas_s_entrada') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'empresas_s_entrada');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100 p-2">
                    <Package color="#F59E0B" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Sem Entrada
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.empresas_s_entrada ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.empresas_s_entrada_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>
            </div>

            {/* Carrossel Inferior - Status de Comparação */}
            <div className="mb-4 flex flex-row flex-nowrap gap-3 overflow-x-auto pb-2">
              {/* Card OK Saída */}
              <Tooltip
                label="OK Saída: Todas as fontes BATEM com Domínio. NFE/NFCE/CTE: SIEG=DOM=Autmais. NFSE: varia por cidade/UF (ignora SIEG para GO/DF, ignora Portal para SP/Goiânia)"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'ok_saida' ? 'bg-[#DCFAF8]' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'ok_saida') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'ok_saida');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DCFAF8] p-2">
                    <CiCircleCheck color="#16DBCC" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      OK Saída
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.ok_saida ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.ok_saida_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card OK Entrada */}
              <Tooltip
                label="OK Entrada: Todas as fontes BATEM. NFE/NFCE/CTE: SIEG=DOM. NFSE: Autmais=DOM=Portal"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'ok_entrada' ? 'bg-[#DCFAF8]' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'ok_entrada') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'ok_entrada');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#DCFAF8] p-2">
                    <CiCircleCheck color="#16DBCC" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      OK Entrada
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.ok_entrada ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.ok_entrada_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Erro Saída */}
              <Tooltip
                label="Erro Saída: Fonte externa tem MAIS que Domínio. NFE/NFCE: SIEG>DOM ou Autmais>DOM. CTE: SIEG>DOM. NFSE: varia por cidade/UF"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'erro_saida' ? 'bg-[#FFE0EB]' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'erro_saida') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'erro_saida');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFE0EB] p-2">
                    <IoCloseCircleOutline color="#FF82AC" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Erro Saída
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.erro_saida ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.erro_saida_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Erro Entrada */}
              <Tooltip
                label="Erro Entrada: Fonte externa tem MAIS que Domínio. NFE/NFCE/CTE: SIEG>DOM. NFSE: Autmais>DOM ou Portal>DOM"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'erro_entrada' ? 'bg-[#FFE0EB]' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'erro_entrada') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'erro_entrada');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFE0EB] p-2">
                    <IoCloseCircleOutline color="#FF82AC" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Erro Entrada
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.erro_entrada ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.erro_entrada_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Aviso Saída */}
              <Tooltip
                label="Aviso Saída: Domínio tem MAIS que fontes externas. NFE/NFCE: DOM>SIEG e DOM>Autmais. CTE: DOM>SIEG. NFSE: varia por cidade/UF"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'aviso_saida' ? 'bg-[#FFF4E6]' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'aviso_saida') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'aviso_saida');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF4E6] p-2">
                    <Package color="#F59E0B" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Aviso Saída
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.aviso_saida ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.aviso_saida_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>

              {/* Card Aviso Entrada */}
              <Tooltip
                label="Aviso Entrada: Domínio tem MAIS que fontes externas. NFE/NFCE/CTE: DOM>SIEG. NFSE: DOM>Autmais ou DOM>Portal"
                placement="top"
              >
                <Flex
                  className={`flex cursor-pointer items-center gap-2 rounded-2xl p-3 ${
                    filters.get('situacao') === 'aviso_entrada' ? 'bg-[#FFF4E6]' : 'bg-body'
                  }`}
                  onClick={() => {
                    const newFilters = new URLSearchParams(Object.fromEntries(filters));
                    if (filters.get('situacao') === 'aviso_entrada') {
                      newFilters.delete('situacao');
                    } else {
                      newFilters.set('page', '1');
                      newFilters.set('situacao', 'aviso_entrada');
                    }
                    setFilters(newFilters);
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FFF4E6] p-2">
                    <Package color="#F59E0B" className="size-5" />
                  </div>
                  <div className="flex w-full flex-col">
                    <Text fontSize="12px" color="#718EBF">
                      Aviso Entrada
                    </Text>
                    <Text fontSize="16px" fontWeight="bold">
                      {quebrasTotais?.aviso_entrada ?? 0}
                    </Text>
                    <Text fontSize="12px" color="#718EBF">
                      {quebrasTotais?.aviso_entrada_pct ?? 0}%
                    </Text>
                  </div>
                </Flex>
              </Tooltip>
            </div>
          </>
        )}
      </div>

      <FormBox>
        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col justify-between gap-2">
            <FormProvider {...filtersFormMethods}>
              <Flex
                direction={{ base: 'column', md: 'row' }}
                justify="space-between"
                align={{ base: 'stretch', md: 'center' }}
                gap={4}
              >
                {/* Campos de Pesquisa */}
                <HStack spacing={2} flex={1} maxW={{ base: '100%', md: '500px' }}>
                  <SearchModeBar
                    modes={MODES_ID_NOME_CNPJ}
                    mode={searchMode}
                    onModeChange={m => setSearchMode(m as 'ID' | 'Nome' | 'CNPJ')}
                    value={filtersFormMethods.watch('search') ?? ''}
                    onValueChange={v =>
                      filtersFormMethods.setValue('search', v, { shouldDirty: true })
                    }
                  />
                </HStack>

                {/* Navegação de Mês e Botão Filtros */}
                <HStack spacing={3} flexWrap="wrap">
                  <MonthNavigator
                    currentDate={currentDate}
                    onPrevMonth={handlePrevMonth}
                    onNextMonth={handleNextMonth}
                    onSelectMonth={handleSelectMonth}
                  />
                  <Button
                    onClick={() => setIsFilterDrawerOpen(true)}
                    leftIcon={<FunnelSimple size={16} />}
                    variant="outline"
                    colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                    height="38px"
                  >
                    Filtros{' '}
                    {hasActiveFilters &&
                      `(${Array.from(filters.keys()).filter(key => key !== 'page' && key !== 'size' && key !== 'screen_data' && key !== 'date_start' && key !== 'date_end' && key !== 'situacao' && key !== 'search' && key !== 'cnpj' && filters.get(key)).length})`}
                  </Button>
                </HStack>
              </Flex>

              {/* FilterDrawer para filtros avançados */}
              <FilterDrawer
                isOpen={isFilterDrawerOpen}
                onClose={() => setIsFilterDrawerOpen(false)}
                title="Filtros Avançados"
                hasActiveFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
                onApplyFilters={handleApplyFilters}
                formMethods={filtersFormMethods}
                size="lg"
              >
                <div className="grid grid-cols-2 gap-4">
                  <SelectInputForm<ISelect<string>>
                    name="cidade"
                    placeholder="Filtrar por cidade"
                    options={getFormattedLabelString(CompaniesFilters?.cidade)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="uf"
                    placeholder="Filtrar por Estado"
                    options={getFormattedLabelString(CompaniesFilters?.uf)}
                    controlMb={0}
                  />
                  <MultiSelectInputForm<ISelect<string>>
                    name="grupo_de_empresas"
                    placeholder="Selecione até 4 grupos"
                    options={getFormattedLabelString(CompaniesFilters?.grupo_de_empresas)}
                    maxSelectedOptions={4}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="tags"
                    placeholder="Filtrar por tags"
                    options={getFormattedLabelString(CompaniesFilters?.tags)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="comercial"
                    placeholder="Filtrar por comercial"
                    options={getFormattedLabelString(CompaniesFilters?.comercial)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="contabil"
                    placeholder="Filtrar pelo contábil"
                    options={getFormattedLabelString(CompaniesFilters?.contabil)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="financeiro"
                    placeholder="Filtrar pelo financeiro"
                    options={getFormattedLabelString(CompaniesFilters?.financeiro)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="contas_a_pagar"
                    placeholder="Filtrar por contas à pagar"
                    options={getFormattedLabelString(CompaniesFilters?.contas_a_pagar)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="encerramento_anual"
                    placeholder="Filtrar por encerramento anual"
                    options={getFormattedLabelString(CompaniesFilters?.encerramento_anual)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="fiscal"
                    placeholder="Filtrar por fiscal"
                    options={getFormattedLabelString(CompaniesFilters?.fiscal)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="imposto_de_renda"
                    placeholder="filtrar por imposto de renda"
                    options={getFormattedLabelString(CompaniesFilters?.imposto_de_renda)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="integracao_contabil"
                    placeholder="Filtrar por integração contabil"
                    options={getFormattedLabelString(CompaniesFilters?.integracao_contabil)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="integracao_fiscal"
                    placeholder="Filtrar por integração fiscal"
                    options={getFormattedLabelString(CompaniesFilters?.integracao_fiscal)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="pessoal"
                    placeholder="Filtrar por pessoal"
                    options={getFormattedLabelString(CompaniesFilters?.pessoal)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="relacionamento_com_cliente"
                    placeholder="Filtrar por relacionamento com cliente"
                    options={getFormattedLabelString(CompaniesFilters?.relacionamento_com_cliente)}
                    controlMb={0}
                  />
                  <SelectInputForm<ISelect<string>>
                    name="societario"
                    placeholder="Filtrar por societário"
                    options={getFormattedLabelString(CompaniesFilters?.societario)}
                    controlMb={0}
                  />
                </div>
              </FilterDrawer>
            </FormProvider>
          </div>

          {/* Grid de Empresas - 4 por linha */}
          {isFetchingCompanyList ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Spinner size="xl" color="primary" />
              <Text mt={4} fontSize="lg" fontWeight="semibold">
                Carregando Empresas...
              </Text>
            </div>
          ) : companiesList.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Text fontSize="lg" color="gray.500">
                Nenhuma empresa encontrada.
              </Text>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {companiesList.map(item => (
                  <div
                    key={item.id}
                    className="relative rounded-lg border border-gray-300 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
                  >
                    {/* Botão de Notas - Canto Superior Esquerdo */}
                    <div className="absolute left-2 top-2">
                      <ViewNotaModal
                        screen="api_sieg"
                        id={item?.id}
                        dateStart={dateStart}
                        dateEnd={dateEnd}
                        defaultTab="api_sieg"
                        companyName={item?.razao_social}
                      >
                        <Button
                          variant="outline"
                          h="fit-content"
                          w="fit-content"
                          cursor="pointer"
                          borderRadius="full"
                          border="1px solid"
                          borderColor="#718EBF"
                          bg="white"
                          p={1.5}
                          size="xs"
                          minW="auto"
                        >
                          <MdStickyNote2 size={16} color="#718EBF" />
                        </Button>
                      </ViewNotaModal>
                    </div>

                    <div className="flex w-full flex-col items-center gap-2">
                      {/* Header: ID + Nome + CNPJ */}
                      <div className="flex w-full flex-col items-center gap-1">
                        <Text fontWeight="bold" fontSize="sm" color="gray.500">
                          ID: {item.external_id}
                        </Text>

                        <div className="flex w-full flex-col items-center gap-0.5 px-8">
                          <Tooltip label={item.razao_social} placement="top">
                            <Text
                              fontWeight="semibold"
                              fontSize="sm"
                              lineHeight="1.3"
                              noOfLines={1}
                              textAlign="center"
                            >
                              {item.razao_social}
                            </Text>
                          </Tooltip>
                          <Text fontSize="sm" color="gray.600">
                            {formatCNPJ(item.cnpj)}
                          </Text>
                        </div>
                      </div>

                      {/* Cards de Notas */}
                      {(() => {
                        const situacao = filters.get('situacao');
                        const isErro = situacao === 'erro_saida' || situacao === 'erro_entrada';
                        const isAviso = situacao === 'aviso_saida' || situacao === 'aviso_entrada';
                        const highlightColor = isErro
                          ? 'red.500'
                          : isAviso
                            ? 'orange.500'
                            : undefined;
                        const showHighlight = isErro || isAviso;

                        // Regras de cidades/UFs onde fontes devem ser ignoradas
                        const cidadesSemSieg = [
                          'GOIANIA',
                          'GOIÂNIA',
                          'ANAPOLIS',
                          'ANÁPOLIS',
                          'APARECIDA DE GOIANIA',
                          'APARECIDA DE GOIÂNIA',
                          'BRASILIA',
                          'BRASÍLIA',
                        ];
                        const cidadesSemPortal = [...cidadesSemSieg, 'SAO PAULO', 'SÃO PAULO'];
                        const ufsSemSieg = ['GO', 'DF', 'SP'];

                        const cidadeUpper = (item.cidade || '').toUpperCase();
                        const ufUpper = (item.uf || '').toUpperCase();

                        // Flags de ignorar
                        // NFE e NFCE: ignora SIEG se UF for GO, DF ou SP
                        const ignorarSiegNfeNfce = ufsSemSieg.includes(ufUpper);
                        // NFSE: ignora SIEG se cidade ou UF estiver na lista
                        const ignorarSiegNfse =
                          cidadesSemSieg.includes(cidadeUpper) || ufsSemSieg.includes(ufUpper);
                        // NFSE: ignora Portal se cidade estiver na lista
                        const ignorarPortalNfse = cidadesSemPortal.includes(cidadeUpper);

                        // Funções para verificar divergência
                        // NFE Entrada: compara com SIEG e Autmais
                        const hasNfeEntradaDivergencia = () => {
                          const dominio = item.qt_notas_dominio?.nfe_entradas ?? 0;
                          const sieg = item.qt_notas_sieg?.nfe_entradas ?? 0;
                          const autmais = item.qt_notas_autmais?.nfe_entradas ?? 0;

                          if (ignorarSiegNfeNfce) {
                            return dominio !== autmais;
                          }
                          return dominio !== sieg || dominio !== autmais;
                        };

                        // NFCE Entrada: compara com SIEG (não tem Autmais entrada)
                        const hasNfceEntradaDivergencia = () => {
                          const dominio = item.qt_notas_dominio?.nfce_entradas ?? 0;
                          const sieg = item.qt_notas_sieg?.nfce_entradas ?? 0;
                          if (!ignorarSiegNfeNfce && dominio !== sieg) return true;
                          return false;
                        };

                        // CTE: sempre compara com SIEG (Autmais não tem CTE)
                        const hasCteEntradaDivergencia = () => {
                          const dominio = item.qt_notas_dominio?.cte_entradas ?? 0;
                          const sieg = item.qt_notas_sieg?.cte_entradas ?? 0;
                          return dominio !== sieg;
                        };

                        const hasCteSaidaDivergencia = () => {
                          const dominio = item.qt_notas_dominio?.cte_saidas ?? 0;
                          const sieg = item.qt_notas_sieg?.cte_saidas ?? 0;
                          return dominio !== sieg;
                        };

                        // NFE Saída: compara com SIEG ou Autmais
                        const hasNfeSaidaDivergencia = () => {
                          const dominio = item.qt_notas_dominio?.nfe_saidas ?? 0;
                          const sieg = item.qt_notas_sieg?.nfe_saidas ?? 0;
                          const autmais = item.qt_notas_autmais?.nfe_saidas ?? 0;

                          if (ignorarSiegNfeNfce) {
                            // Se ignora SIEG, compara só com Autmais
                            return dominio !== autmais;
                          }
                          // Se não ignora, compara com ambos
                          return dominio !== sieg || dominio !== autmais;
                        };

                        // NFCE Saída: compara com SIEG ou Autmais
                        const hasNfceSaidaDivergencia = () => {
                          const dominio = item.qt_notas_dominio?.nfce_saidas ?? 0;
                          const sieg = item.qt_notas_sieg?.nfce_saidas ?? 0;
                          const autmais = item.qt_notas_autmais?.nfce_saidas ?? 0;

                          if (ignorarSiegNfeNfce) {
                            return dominio !== autmais;
                          }
                          return dominio !== sieg || dominio !== autmais;
                        };

                        // NFSE Entrada
                        const hasNfseEntradaDivergencia = () => {
                          const dominio = item.qt_notas_dominio?.nfse_entradas ?? 0;
                          const sieg = item.qt_notas_sieg?.nfse_entradas ?? 0;
                          const autmais = item.qt_notas_autmais?.nfse_entradas ?? 0;
                          const portal = item.qt_notas_portal_nacional?.nfse_entradas ?? 0;

                          // Sempre compara com Autmais
                          if (dominio !== autmais) return true;
                          // Compara com SIEG se não deve ignorar
                          if (!ignorarSiegNfse && dominio !== sieg) return true;
                          // Compara com Portal se não deve ignorar
                          if (!ignorarPortalNfse && dominio !== portal) return true;

                          return false;
                        };

                        // NFSE Saída
                        const hasNfseSaidaDivergencia = () => {
                          const dominio = item.qt_notas_dominio?.nfse_saidas ?? 0;
                          const sieg = item.qt_notas_sieg?.nfse_saidas ?? 0;
                          const autmais = item.qt_notas_autmais?.nfse_saidas ?? 0;
                          const portal = item.qt_notas_portal_nacional?.nfse_saidas ?? 0;

                          // Sempre compara com Autmais
                          if (dominio !== autmais) return true;
                          // Compara com SIEG se não deve ignorar
                          if (!ignorarSiegNfse && dominio !== sieg) return true;
                          // Compara com Portal se não deve ignorar
                          if (!ignorarPortalNfse && dominio !== portal) return true;

                          return false;
                        };

                        return (
                          <div className="grid w-full grid-cols-2 gap-2 md:grid-cols-4 md:gap-4">
                            {/* Domínio */}
                            <Tooltip
                              label="Dados do sistema Domínio: Compara quantidade de notas fiscais (NFE, NFCE, CTE, NFSE) de entrada e saída registradas no sistema interno. Valores coloridos indicam divergência com outras fontes."
                              placement="top"
                            >
                              <div className="flex-1 rounded-lg border border-gray-200 bg-gray-50 p-2 text-center">
                                <Text fontSize="sm" fontWeight="bold" color="blue.600" mb={1}>
                                  Domínio
                                </Text>
                                <div className="flex flex-col text-xs leading-relaxed">
                                  <Text fontWeight="semibold" fontSize="sm">
                                    Entrada:
                                  </Text>
                                  <Text
                                    color={
                                      showHighlight && hasNfeEntradaDivergencia()
                                        ? highlightColor
                                        : undefined
                                    }
                                    fontWeight={
                                      showHighlight && hasNfeEntradaDivergencia()
                                        ? 'bold'
                                        : undefined
                                    }
                                  >
                                    NFE: {item.qt_notas_dominio?.nfe_entradas ?? 0}
                                  </Text>
                                  <Text
                                    color={
                                      showHighlight && hasNfceEntradaDivergencia()
                                        ? highlightColor
                                        : undefined
                                    }
                                    fontWeight={
                                      showHighlight && hasNfceEntradaDivergencia()
                                        ? 'bold'
                                        : undefined
                                    }
                                  >
                                    NFCE: {item.qt_notas_dominio?.nfce_entradas ?? 0}
                                  </Text>
                                  <Text
                                    color={
                                      showHighlight && hasCteEntradaDivergencia()
                                        ? highlightColor
                                        : undefined
                                    }
                                    fontWeight={
                                      showHighlight && hasCteEntradaDivergencia()
                                        ? 'bold'
                                        : undefined
                                    }
                                  >
                                    CTE: {item.qt_notas_dominio?.cte_entradas ?? 0}
                                  </Text>
                                  <Text
                                    color={
                                      showHighlight && hasNfseEntradaDivergencia()
                                        ? highlightColor
                                        : undefined
                                    }
                                    fontWeight={
                                      showHighlight && hasNfseEntradaDivergencia()
                                        ? 'bold'
                                        : undefined
                                    }
                                  >
                                    NFSE: {item.qt_notas_dominio?.nfse_entradas ?? 0}
                                  </Text>
                                  <Text fontWeight="semibold" fontSize="sm" mt={1}>
                                    Saída:
                                  </Text>
                                  <Text
                                    color={
                                      showHighlight && hasNfeSaidaDivergencia()
                                        ? highlightColor
                                        : undefined
                                    }
                                    fontWeight={
                                      showHighlight && hasNfeSaidaDivergencia() ? 'bold' : undefined
                                    }
                                  >
                                    NFE: {item.qt_notas_dominio?.nfe_saidas ?? 0}
                                  </Text>
                                  <Text
                                    color={
                                      showHighlight && hasNfceSaidaDivergencia()
                                        ? highlightColor
                                        : undefined
                                    }
                                    fontWeight={
                                      showHighlight && hasNfceSaidaDivergencia()
                                        ? 'bold'
                                        : undefined
                                    }
                                  >
                                    NFCE: {item.qt_notas_dominio?.nfce_saidas ?? 0}
                                  </Text>
                                  <Text
                                    color={
                                      showHighlight && hasCteSaidaDivergencia()
                                        ? highlightColor
                                        : undefined
                                    }
                                    fontWeight={
                                      showHighlight && hasCteSaidaDivergencia() ? 'bold' : undefined
                                    }
                                  >
                                    CTE: {item.qt_notas_dominio?.cte_saidas ?? 0}
                                  </Text>
                                  <Text
                                    color={
                                      showHighlight && hasNfseSaidaDivergencia()
                                        ? highlightColor
                                        : undefined
                                    }
                                    fontWeight={
                                      showHighlight && hasNfseSaidaDivergencia()
                                        ? 'bold'
                                        : undefined
                                    }
                                  >
                                    NFSE: {item.qt_notas_dominio?.nfse_saidas ?? 0}
                                  </Text>
                                  <Text fontWeight="semibold" fontSize="sm" mt={1}>
                                    Total: {item.qt_notas_dominio?.total ?? 0}
                                  </Text>
                                </div>
                              </div>
                            </Tooltip>

                            {/* SIEG */}
                            <Tooltip
                              label="Dados da Receita Federal (SIEG): Quantidade oficial de notas fiscais registradas na Receita. Usado para validar se todas as notas do Domínio foram transmitidas corretamente"
                              placement="top"
                            >
                              <div className="flex-1 rounded-lg border border-gray-200 bg-green-50 p-2 text-center">
                                <Text fontSize="sm" fontWeight="bold" color="green.600" mb={1}>
                                  SIEG
                                </Text>
                                <div className="flex flex-col text-xs leading-relaxed">
                                  <Text fontWeight="semibold" fontSize="sm">
                                    Entrada:
                                  </Text>
                                  <Text>NFE: {item.qt_notas_sieg?.nfe_entradas ?? 0}</Text>
                                  <Text>NFCE: {item.qt_notas_sieg?.nfce_entradas ?? 0}</Text>
                                  <Text>CTE: {item.qt_notas_sieg?.cte_entradas ?? 0}</Text>
                                  <Text>NFSE: {item.qt_notas_sieg?.nfse_entradas ?? 0}</Text>
                                  <Text fontWeight="semibold" fontSize="sm" mt={1}>
                                    Saída:
                                  </Text>
                                  <Text>NFE: {item.qt_notas_sieg?.nfe_saidas ?? 0}</Text>
                                  <Text>NFCE: {item.qt_notas_sieg?.nfce_saidas ?? 0}</Text>
                                  <Text>CTE: {item.qt_notas_sieg?.cte_saidas ?? 0}</Text>
                                  <Text>NFSE: {item.qt_notas_sieg?.nfse_saidas ?? 0}</Text>
                                  <Text fontWeight="semibold" fontSize="sm" mt={1}>
                                    Total: {item.qt_notas_sieg?.total ?? 0}
                                  </Text>
                                </div>
                              </div>
                            </Tooltip>

                            {/* Autmais */}
                            <Tooltip
                              label="Dados do Autmais (RPA): Quantidade de notas fiscais capturadas pelo sistema de automação. Usado para validar se todas as notas foram importadas corretamente"
                              placement="top"
                            >
                              <div className="flex-1 rounded-lg border border-gray-200 bg-orange-50 p-2 text-center">
                                <Text fontSize="sm" fontWeight="bold" color="orange.600" mb={1}>
                                  Autmais
                                </Text>
                                <div className="flex flex-col text-xs leading-relaxed">
                                  <Text fontWeight="semibold" fontSize="sm">
                                    Entrada:
                                  </Text>
                                  <Text>NFE: {item.qt_notas_autmais?.nfe_entradas ?? 0}</Text>
                                  <Text>NFCE: -</Text>
                                  <Text>CTE: -</Text>
                                  <Text>NFSE: {item.qt_notas_autmais?.nfse_entradas ?? 0}</Text>
                                  <Text fontWeight="semibold" fontSize="sm" mt={1}>
                                    Saída:
                                  </Text>
                                  <Text>NFE: {item.qt_notas_autmais?.nfe_saidas ?? 0}</Text>
                                  <Text>NFCE: {item.qt_notas_autmais?.nfce_saidas ?? 0}</Text>
                                  <Text>CTE: -</Text>
                                  <Text>NFSE: {item.qt_notas_autmais?.nfse_saidas ?? 0}</Text>
                                  <Text fontWeight="semibold" fontSize="sm" mt={1}>
                                    Total: {item.qt_notas_autmais?.total ?? 0}
                                  </Text>
                                </div>
                              </div>
                            </Tooltip>

                            {/* Portal Nacional */}
                            <Tooltip
                              label="Dados do Portal Nacional NFS-e: Quantidade de notas fiscais de serviço registradas no portal nacional. Prestado = Saída, Tomado = Entrada. Filtro aplicado: Data de Emissão"
                              placement="top"
                            >
                              <div className="flex-1 rounded-lg border border-gray-200 bg-purple-50 p-2 text-center">
                                <Text fontSize="sm" fontWeight="bold" color="purple.600" mb={1}>
                                  Portal Nac.
                                </Text>
                                <div className="flex flex-col text-xs leading-relaxed">
                                  <Text fontWeight="semibold" fontSize="sm">
                                    Entrada:
                                  </Text>
                                  <Text>NFE: -</Text>
                                  <Text>NFCE: -</Text>
                                  <Text>CTE: -</Text>
                                  <Text>
                                    NFSE: {item.qt_notas_portal_nacional?.nfse_entradas ?? 0}
                                  </Text>
                                  <Text fontWeight="semibold" fontSize="sm" mt={1}>
                                    Saída:
                                  </Text>
                                  <Text>NFE: -</Text>
                                  <Text>NFCE: -</Text>
                                  <Text>CTE: -</Text>
                                  <Text>
                                    NFSE: {item.qt_notas_portal_nacional?.nfse_saidas ?? 0}
                                  </Text>
                                  <Text fontWeight="semibold" fontSize="sm" mt={1}>
                                    Total: {item.qt_notas_portal_nacional?.total ?? 0}
                                  </Text>
                                </div>
                              </div>
                            </Tooltip>
                          </div>
                        );
                      })()}

                      {/* UF e Município */}
                      <div className="mt-2 flex justify-center gap-4">
                        <Text fontSize="xs" color="gray.500">
                          <Text as="span" fontWeight="semibold">
                            UF:
                          </Text>{' '}
                          {item.uf || '-'}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          <Text as="span" fontWeight="semibold">
                            Município:
                          </Text>{' '}
                          {item.cidade || '-'}
                        </Text>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Paginação */}
              {pagination && (
                <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                  <div className="flex items-center gap-2">
                    <Text fontSize="sm" color="gray.600">
                      Mostrando {(pagination.page - 1) * pagination.size + 1} a{' '}
                      {Math.min(pagination.page * pagination.size, pagination.total)} de{' '}
                      {pagination.total} empresas
                    </Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        filters.set('page', String(pagination.page - 1));
                        setFilters(filters);
                      }}
                      isDisabled={pagination.page === 1}
                    >
                      Anterior
                    </Button>
                    <Text fontSize="sm" fontWeight="semibold">
                      Página {pagination.page} de {pagination.pages}
                    </Text>
                    <Button
                      size="sm"
                      onClick={() => {
                        filters.set('page', String(pagination.page + 1));
                        setFilters(filters);
                      }}
                      isDisabled={pagination.page === pagination.pages}
                    >
                      Próxima
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </FormBox>
    </PageBox>
  );
}
